const HAS_LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;
const FIELD_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const CATEGORIES = ["General", "Care Coordination", "Programs", "PBM", "Fees", "Risk"];
const DATA_TYPES = ["string", "number", "date", "boolean"];
const SCOPES = [
    { value: "client", label: "Client" },
    { value: "plan", label: "Plan" },
];
const PRIORITY_RULES = [
    { value: "first-found", label: "First found" },
    { value: "last-found", label: "Last found" },
    { value: "most-prominent", label: "Most prominent" },
    { value: "header-only", label: "Header only" },
];

const fieldKeyFromLabel = (label) =>
    String(label ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const lines = (value) =>
    String(value ?? "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

const joinLines = (values) => (Array.isArray(values) ? values.join("\n") : "");

const blankAssignment = (documentTypeKey) => ({
    documentTypeKey,
    selected: false,
    required: false,
    extractionOrder: "0",
    notes: "",
    overrideWhereToLook: "",
    overrideExamples: "",
    overrideExtractionNotes: "",
    overrideFormatPattern: "",
    overridePriorityRule: "",
    overrideConfidenceThreshold: "",
});

const assignmentFromStored = (stored) => {
    const overrides = stored.overrides ?? {};
    return {
        ...blankAssignment(stored.documentTypeKey),
        selected: true,
        required: stored.required === true,
        extractionOrder: String(stored.extractionOrder ?? 0),
        notes: stored.notes ?? "",
        overrideWhereToLook: overrides.whereToLook ?? "",
        overrideExamples: joinLines(overrides.examples),
        overrideExtractionNotes: overrides.extractionNotes ?? "",
        overrideFormatPattern: overrides.formatPattern ?? "",
        overridePriorityRule: overrides.priorityRule ?? "",
        overrideConfidenceThreshold:
            overrides.confidenceThreshold == null ? "" : String(overrides.confidenceThreshold),
    };
};

const formValuesFromField = (field, documentTypes = []) => {
    const assigned = new Map((field?.documentTypes ?? []).map((item) => [item.documentTypeKey, item]));
    const listed = new Set();
    const rows = [];
    for (const documentType of documentTypes) {
        if (documentType.status !== "ACTIVE" && !assigned.has(documentType.documentTypeKey)) continue;
        listed.add(documentType.documentTypeKey);
        const stored = assigned.get(documentType.documentTypeKey);
        rows.push(stored ? assignmentFromStored(stored) : blankAssignment(documentType.documentTypeKey));
    }
    for (const [documentTypeKey, stored] of assigned) {
        if (!listed.has(documentTypeKey)) rows.push(assignmentFromStored(stored));
    }
    return {
        fieldKey: field?.fieldKey ?? "",
        displayName: field?.displayName ?? "",
        status: field?.status ?? "ACTIVE",
        dataType: field?.dataType ?? "string",
        category: field?.category ?? "General",
        scope: field?.scope ?? "client",
        universal: field?.universal === true,
        confidenceThreshold: field?.confidenceThreshold ?? 0.8,
        commonLabels: joinLines(field?.commonLabels),
        whereToLook: field?.whereToLook ?? "",
        examples: joinLines(field?.examples),
        extractionNotes: field?.extractionNotes ?? "",
        formatPattern: field?.formatPattern ?? "",
        priorityRule: field?.priorityRule ?? "first-found",
        sortOrder: field?.sortOrder ?? 0,
        documentTypes: rows,
    };
};

const assignmentInputError = (assignment, field) => {
    if (!assignment?.selected) return "";
    if (field === "extractionOrder") {
        const order = Number(assignment.extractionOrder);
        if (!Number.isInteger(order) || order < 0) {
            return "Extraction order must be a non-negative whole number.";
        }
    }
    if (field === "overrideConfidenceThreshold") {
        const raw = assignment.overrideConfidenceThreshold;
        if (raw !== "" && raw != null) {
            const threshold = Number(raw);
            if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
                return "An override confidence threshold must be a number from 0 through 1, or blank to inherit.";
            }
        }
    }
    return "";
};

const validateFieldForm = (values, { requireKey = false } = {}) => {
    const errors = {};
    const displayName = values.displayName?.trim() ?? "";
    if (!displayName) errors.displayName = "Display name is required.";
    else if (!HAS_LETTER_OR_NUMBER.test(displayName)) {
        errors.displayName = "Display name must contain a letter or number.";
    }
    if (requireKey) {
        const fieldKey = values.fieldKey?.trim() ?? "";
        if (!FIELD_KEY_PATTERN.test(fieldKey)) {
            errors.fieldKey = "Key must use lowercase letters, numbers, and hyphens.";
        }
    }
    if (!CATEGORIES.includes(values.category)) errors.category = "Category is required.";
    if (!DATA_TYPES.includes(values.dataType)) errors.dataType = "Data type is required.";
    if (!SCOPES.some((scope) => scope.value === values.scope)) errors.scope = "Scope is required.";
    const threshold = Number(values.confidenceThreshold);
    if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
        errors.confidenceThreshold = "Confidence threshold must be a number from 0 through 1.";
    }
    const sortOrder = String(values.sortOrder ?? "").trim();
    if (sortOrder !== "" && (!Number.isInteger(Number(sortOrder)) || Number(sortOrder) < 0)) {
        errors.sortOrder = "Sort order must be a non-negative whole number.";
    }
    const documentTypes = values.documentTypes ?? [];
    if (!documentTypes.some((assignment) => assignment.selected)) {
        errors.assignments = "At least one document type is required.";
    }
    for (const assignment of documentTypes) {
        if (!assignment.selected) continue;
        const raw = assignment.overrideConfidenceThreshold;
        if (raw !== "" && raw != null) {
            const threshold = Number(raw);
            if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
                errors.assignments = "An override confidence threshold must be a number from 0 through 1, or blank to inherit.";
            }
        }
        if (!Number.isInteger(Number(assignment.extractionOrder)) || Number(assignment.extractionOrder) < 0) {
            errors.assignments = "Extraction order must be a non-negative whole number.";
        }
    }
    return errors;
};

const buildAssignmentBody = (assignment) => {
    const overrides = {};
    const whereToLook = assignment.overrideWhereToLook?.trim();
    const extractionNotes = assignment.overrideExtractionNotes?.trim();
    const formatPattern = assignment.overrideFormatPattern?.trim();
    const examples = lines(assignment.overrideExamples);
    if (whereToLook) overrides.whereToLook = whereToLook;
    if (extractionNotes) overrides.extractionNotes = extractionNotes;
    if (formatPattern) overrides.formatPattern = formatPattern;
    if (examples.length) overrides.examples = examples;
    if (assignment.overridePriorityRule) overrides.priorityRule = assignment.overridePriorityRule;
    if (assignment.overrideConfidenceThreshold !== "" && assignment.overrideConfidenceThreshold != null) {
        overrides.confidenceThreshold = Number(assignment.overrideConfidenceThreshold);
    }
    return {
        documentTypeKey: assignment.documentTypeKey,
        required: Boolean(assignment.required),
        extractionOrder: Number(assignment.extractionOrder) || 0,
        notes: assignment.notes?.trim() ?? "",
        overrides,
    };
};

const buildFieldBody = (values) => ({
    ...(values.fieldKey ? { fieldKey: values.fieldKey.trim() } : {}),
    displayName: values.displayName.trim(),
    status: values.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
    dataType: values.dataType,
    category: values.category,
    scope: values.scope,
    universal: Boolean(values.universal),
    confidenceThreshold: Number(values.confidenceThreshold),
    commonLabels: lines(values.commonLabels),
    whereToLook: values.whereToLook?.trim() ?? "",
    examples: lines(values.examples),
    extractionNotes: values.extractionNotes?.trim() ?? "",
    formatPattern: values.formatPattern?.trim() ?? "",
    priorityRule: values.priorityRule || "first-found",
    sortOrder: Number(values.sortOrder) || 0,
    documentTypes: (values.documentTypes ?? []).filter((assignment) => assignment.selected).map(buildAssignmentBody),
});

export {
    CATEGORIES,
    DATA_TYPES,
    PRIORITY_RULES,
    SCOPES,
    assignmentInputError,
    buildFieldBody,
    fieldKeyFromLabel,
    formValuesFromField,
    joinLines,
    validateFieldForm,
};
